import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, FolderPlus, FlaskConical, ChevronsDownUp, ChevronsUpDown, X, Archive, Check, TrendingDown, PackageX, Calculator, Gift } from 'lucide-react';
import RecipeCategoryTree from './RecipeCategoryTree';
import RecipeListRow from './RecipeListRow';
import { KANALY } from './PrehledBunky';
import { GRID, PRAH_MARZE, nejhorsiMarze, MARZE_BARVY, useViditelneSloupce } from './prehledGrid';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry, pocetAktivnichFiltru } from '../layout/ListFiltr';
import { SECTION_COLOR, recVariantKey } from './receptUtils';
import { useVyberPolozek, pocetPolozek } from './useVyberPolozek';

const BEZ_KAT = '__bez__';

// ── Filtr seznamu ────────────────────────────────────────────────────────────
// Hodnoty skupin: volby = pole (NEBO uvnitř skupiny), kategorie = jedno id.
const PRAZDNE_FILTRY = { kategorie: null, kanal: [], marze: [], prodej: [], stav: [], revize: [] };
const SEST_MESICU_MS = 183 * 86400000; // stejná hranice jako validace receptury na backendu

export const pasmoMarze = (r) => {
    const m = nejhorsiMarze(r.marze_kanaly);
    if (m == null) return 'bez';
    if (m < PRAH_MARZE.kriticka) return 'pod';
    return m < PRAH_MARZE.dobra ? 'stredni' : 'nad';
};
export const stavReceptury = (r) => (r.validace?.stav === 'red' ? 'chyba' : r.validace?.stav === 'orange' ? 'varovani' : 'ok');
export const stariRevize = (r) => {
    if (!r.posledni_revize_at) return 'nikdy';
    return Date.now() - new Date(r.posledni_revize_at).getTime() > SEST_MESICU_MS ? 'stara' : 'aktualni';
};

// Strom kategorií → volby Dropdownu (s odsazením podle úrovně).
const kategorieVolby = (nodes, uroven = 0, acc = []) => {
    for (const n of nodes || []) {
        acc.push({ value: n.id, label: n.nazev, uroven });
        if (n.children?.length) kategorieVolby(n.children, uroven + 1, acc);
    }
    return acc;
};

// Všechna ID kategorií ve stromu (rekurzivně) + pseudo-uzel „Bez kategorie".
const collectCatIds = (nodes, acc = []) => {
    for (const n of nodes || []) {
        acc.push(n.id);
        if (n.children?.length) collectCatIds(n.children, acc);
    }
    return acc;
};

// Skeleton řádky — pocit rychlosti při načítání
export function ListSkeleton({ isDark }) {
    const bar = (w, h = 9) => (
        <div style={{ width: w, height: h, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }} />
    );
    return (
        <div className="animate-pulse">
            {[...Array(7)].map((_, i) => (
                <div key={i} className="flex items-center gap-2.5" style={{ padding: '8px 10px' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 7, flexShrink: 0, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }} />
                    <div className="flex-1 space-y-1.5">
                        {bar(`${55 + (i * 7) % 35}%`, 10)}
                        {bar('40%')}
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * Rozbalit / sbalit všechny kategorie. Není to pohled na data, ovládá jen strom —
 * proto sedí v hlavičce seznamu přímo nad šipkami kategorií, které přepíná
 * (v přehledu vlevo ve sloupci šipek, v úzkém režimu vpravo na konci řádku).
 */
export function RozbalitVse({ rozbaleni, mutedColor }) {
    if (!rozbaleni) return null;
    const { vse, prepni } = rozbaleni;
    const Ikona = vse ? ChevronsDownUp : ChevronsUpDown;
    const popis = vse ? 'Sbalit všechny kategorie' : 'Rozbalit všechny kategorie';
    return (
        <button type="button" onClick={prepni} title={popis} aria-label={popis}
            className="flex items-center justify-center rounded-md"
            style={{ width: 24, height: 22, background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 0 }}
            onMouseEnter={e => { e.currentTarget.style.color = SECTION_COLOR; }}
            onMouseLeave={e => { e.currentTarget.style.color = mutedColor; }}>
            <Ikona size={16} />
        </button>
    );
}

/**
 * Hlavička úzkého seznamu (vedle detailu) — počet zobrazených položek a
 * rozbalení stromu zarovnané nad šipky kategorií vpravo.
 */
export function HlavickaUzka({ isDark, pocet, rozbaleni }) {
    const barva = isDark ? 'rgba(255,255,255,0.62)' : 'var(--text-secondary)';
    return (
        <div className="flex items-center justify-between"
            style={{
                minHeight: 30, padding: '4px 7px 4px 13px',
                background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.022)',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
            }}>
            <span className="text-[10px] font-semibold uppercase tracking-wider tabular-nums" style={{ color: barva }}>
                {pocet} {pocet === 1 ? 'položka' : (pocet >= 2 && pocet <= 4 ? 'položky' : 'položek')}
            </span>
            <RozbalitVse rozbaleni={rozbaleni} mutedColor={barva} />
        </div>
    );
}

/**
 * Hlavička sloupcové mřížky — popisky jednou nad seznamem, ne na každém řádku.
 * Šířky si bere z GRID, takže sedí nad hodnotami položek i kategorií.
 *
 * Marže má TŘI čísla vedle sebe (bistro / rozvoz / výdej) a dřív nad nimi
 * stál jeden zalomený popisek „marže bistro / rozvoz / výdej" — u konkrétního
 * čísla se nedalo poznat, který kanál to je, a člověk si musel počítat pořadí.
 * Každý kanál má proto vlastní popisek přesně nad svou buňkou; šířku bere ze
 * stejného výpočtu jako `MarzeBunka` a pořadí ze stejné konstanty `KANALY`,
 * aby se nemohly rozejít.
 *
 * Hlavička je navíc podbarvená — na plochém pozadí splývala s prvním řádkem.
 */
function HlavickaMrizky({ isDark, sloupce, rozbaleni }) {
    const l = 'text-[10px] font-semibold uppercase tracking-wider truncate';
    // Popisky byly v `mutedColor` (35 % bílé) a proti tučným číslům pod nimi
    // se ztrácely; o stupeň víc kontrastu je pořád drží v druhém plánu.
    const barva = isDark ? 'rgba(255,255,255,0.62)' : 'var(--text-secondary)';
    const sirkaMarze = Math.floor((GRID.marze - 8) / 3);

    const bunka = (label, sirka, zarovnani = 'left') => (
        <div key={label} className={l} style={{ width: sirka, flexShrink: 0, textAlign: zarovnani }}>{label}</div>
    );

    return (
        <div className="flex items-end"
            style={{
                gap: GRID.mezera, padding: '7px 10px 6px 13px', color: barva,
                background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.022)',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
            }}>
            {/* Sloupec šipek kategorií → tady je i jejich hromadné rozbalení */}
            <div className="flex items-center justify-center" style={{ width: GRID.uchyt, flexShrink: 0, alignSelf: 'center' }}>
                <RozbalitVse rozbaleni={rozbaleni} mutedColor={barva} />
            </div>
            {sloupce?.nahled !== false && <div style={{ width: GRID.nahled, flexShrink: 0 }} />}
            <div className={`${l}`} style={{ flex: `${GRID.polozkaRust} 1 0`, minWidth: GRID.polozkaMin }}>položka</div>
            {sloupce?.kanaly !== false && (
                <div className={l} style={{ flex: '1 1 auto', minWidth: GRID.kanaly }}>kanály</div>
            )}
            {bunka('náklad → cena', GRID.naklad, 'right')}

            {/* Marže: nadpis skupiny + popisek ke každému kanálu zvlášť */}
            <div style={{ width: GRID.marze, flexShrink: 0 }}>
                <div className="text-[9px] font-semibold uppercase tracking-wider truncate text-center"
                    style={{ opacity: 0.62, marginBottom: 3 }}>marže %</div>
                <div className="flex items-center" style={{ gap: 4 }}>
                    {KANALY.map(k => (
                        <span key={k.id} className={`${l} text-center`} style={{ width: sirkaMarze }}>{k.label}</span>
                    ))}
                </div>
            </div>

            {sloupce?.prodej !== false && bunka('prodej 30 dní', GRID.prodej, 'right')}
            {sloupce?.revize !== false && bunka('revize', GRID.revize, 'right')}
            {bunka('stav', GRID.stav, 'right')}
        </div>
    );
}

/**
 * Seznam polotovarů/produktů — první sloupec standardního rozvržení modulu.
 *
 * `siroky` = seznam běží jako přehled přes celou šířku (nic není vybráno):
 * řádky se rozloží do sloupců a hlavička je v expandované verzi. V úzkém
 * režimu (vedle detailu) je to dvouřádková karta a ikonová hlavička.
 * Rozvržení a šířky řeší ModulLayout, panel se roztáhne do toho, co dostane.
 */
export default function RecipeListPanel({
    typ, isMobile, isDark, mutedColor, secondaryColor, cardStyle, inputStyle,
    recSearch, setRecSearch,
    katStrom, filteredRec, recGrouped, recLoading,
    activeKey, activeId, onSelect, onNew,
    onCatSave, onCatAdd, onCatDelete, onMoveItems, onReparentCat, onReorderCat,
    onAiAnalyza, siroky = false, onKategorieKlik, prodejMap = null,
    onUpravitKategorii = null, aktivniKategorieId = null,
    onSpravovatComba = null,
}) {
    const searchActive = !!recSearch.trim();
    const sloupce = useViditelneSloupce(siroky);
    const jednotneJm = typ === 'SEM' ? 'polotovar' : 'produkt';

    // Vícenásobný výběr pro hromadné přetažení — po přepnutí typu nedává smysl.
    const vyber = useVyberPolozek(typ);

    // Stav rozbalení stromu — sdílený s RecipeCategoryTree.
    // Default = vše sbalené; reset při přepnutí typu (polotovary ↔ produkty).
    const [expanded, setExpanded] = useState(() => new Set());
    useEffect(() => { setExpanded(new Set()); }, [typ]);

    // Pohled = předem definovaná sada dat (pill pod hledáním), vždy právě jeden.
    // Archiv je samostatný pohled, ne uzel navíc — jinak ho v přehledu nikdo nenajde.
    const [pohled, setPohled] = useState('aktivni');
    // Filtr = vlastní zúžení pohledu (tlačítko vedle hledání).
    const [filtry, setFiltry] = useState(PRAZDNE_FILTRY);
    useEffect(() => { setPohled('aktivni'); setFiltry(PRAZDNE_FILTRY); }, [typ]);

    // Založení kořenové kategorie spouští tlačítko v hlavičce (norma: akce
    // vpravo nahoře), ale modal vlastní strom — ten si sem pošle otvírák.
    const otevritNovouKat = useRef(null);
    const registrujNovouKat = useCallback((fn) => { otevritNovouKat.current = fn; }, []);

    const allCatIds = useMemo(() => [...collectCatIds(katStrom), BEZ_KAT], [katStrom]);
    const allExpanded = allCatIds.length > 0 && allCatIds.every(id => expanded.has(id));
    const hasTree = allCatIds.length > 1; // aspoň jedna reálná kategorie
    const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(allCatIds));

    const { aktivniRec, archivRec } = useMemo(() => {
        const akt = [], arch = [];
        for (const r of filteredRec) (r.aktivni === false ? arch : akt).push(r);
        return { aktivniRec: akt, archivRec: arch };
    }, [filteredRec]);

    // Kategorie: efektivní viditelnost v kanálech (stejné dědění jako strom —
    // skrytý předek skryje potomka) a množina potomků pro filtr „Kategorie".
    const katInfo = useMemo(() => {
        const eff = new Map();
        const potomci = new Map();
        const projdi = (nodes, rodic) => {
            for (const n of nodes || []) {
                const e = { pokladna: !!rodic.pokladna && !!n.viditelne_pokladna, eshop: !!rodic.eshop && !!n.viditelne_eshop };
                eff.set(n.id, e);
                projdi(n.children, e);
            }
        };
        projdi(katStrom, { pokladna: true, eshop: true });
        const sber = (n) => {
            const ids = [n.id];
            for (const c of n.children || []) ids.push(...sber(c));
            potomci.set(n.id, new Set(ids));
            return ids;
        };
        for (const n of katStrom || []) sber(n);
        return { eff, potomci };
    }, [katStrom]);

    // Hodnota položky v dané skupině filtru. Kanál může mít víc hodnot naráz.
    const odpovidaVolbe = useCallback((skupina, r, volba) => {
        switch (skupina) {
            case 'kanal': {
                const e = katInfo.eff.get(r.kategorie_id);
                const pokladna = !!e?.pokladna;
                const eshop = !!e?.eshop && r.eshop_viditelny !== false;
                if (volba === 'pokladna') return pokladna;
                if (volba === 'eshop') return eshop;
                return !pokladna && !eshop; // jen v receptáři
            }
            case 'marze': return pasmoMarze(r) === volba;
            case 'prodej': return prodejMap ? (((prodejMap[r.id]?.celkem || 0) > 0 ? 'ano' : 'ne') === volba) : false;
            case 'stav': return stavReceptury(r) === volba;
            case 'revize': return stariRevize(r) === volba;
            default: return true;
        }
    }, [katInfo, prodejMap]);

    // `vynech` = skupina, kterou nepočítat — pro počty u voleb té skupiny.
    const odpovidaFiltrum = useCallback((r, f, vynech = null) => {
        if (vynech !== 'kategorie' && f.kategorie != null) {
            if (!katInfo.potomci.get(f.kategorie)?.has(r.kategorie_id)) return false;
        }
        for (const skupina of ['kanal', 'marze', 'prodej', 'stav', 'revize']) {
            if (skupina === vynech || !f[skupina].length) continue;
            if (!f[skupina].some(v => odpovidaVolbe(skupina, r, v))) return false;
        }
        return true;
    }, [katInfo, odpovidaVolbe]);

    // Předdefinované pohledy. „Neprodává se" až po načtení prodejů.
    const pohledy = useMemo(() => ({
        aktivni: aktivniRec,
        marze: aktivniRec.filter(r => pasmoMarze(r) === 'pod'),
        neprodava: prodejMap ? aktivniRec.filter(r => !((prodejMap[r.id]?.celkem || 0) > 0)) : null,
        bezKalkulace: aktivniRec.filter(r => r.total_cost == null),
        archiv: archivRec,
    }), [aktivniRec, archivRec, prodejMap]);

    const vRezimuArchiv = pohled === 'archiv';
    const pohledSada = pohledy[pohled] || aktivniRec;
    const zobrazene = useMemo(() => pohledSada.filter(r => odpovidaFiltrum(r, filtry)), [pohledSada, filtry, odpovidaFiltrum]);

    const skupinyFiltru = useMemo(() => {
        const sPocty = (id, volby) => volby.map(v => ({
            ...v,
            pocet: pohledSada.filter(r => odpovidaFiltrum(r, filtry, id) && odpovidaVolbe(id, r, v.value)).length,
        }));
        return [
            { id: 'kategorie', label: 'Kategorie', typ: 'dropdown', placeholder: 'Všechny kategorie',
                options: [{ value: '', label: 'Všechny kategorie' }, ...kategorieVolby(katStrom)] },
            { id: 'kanal', label: 'Kanál', typ: 'volby', volby: sPocty('kanal', [
                { value: 'pokladna', label: 'Pokladna' }, { value: 'eshop', label: 'E-shop' }, { value: 'jen', label: 'Jen v receptáři' },
            ]) },
            { id: 'marze', label: 'Marže (nejhorší kanál)', labelKratky: 'Marže', typ: 'volby', volby: sPocty('marze', [
                { value: 'pod', label: `Pod ${PRAH_MARZE.kriticka} %` },
                { value: 'stredni', label: `${PRAH_MARZE.kriticka}–${PRAH_MARZE.dobra} %` },
                { value: 'nad', label: `Nad ${PRAH_MARZE.dobra} %` },
                { value: 'bez', label: 'Bez prodejní ceny' },
            ]) },
            ...(prodejMap ? [{ id: 'prodej', label: 'Prodej za 30 dní', labelKratky: 'Prodej', typ: 'volby', volby: sPocty('prodej', [
                { value: 'ano', label: 'Prodává se' }, { value: 'ne', label: 'Neprodává se' },
            ]) }] : []),
            { id: 'stav', label: 'Stav receptury', labelKratky: 'Stav', typ: 'volby', volby: sPocty('stav', [
                { value: 'ok', label: 'V pořádku' }, { value: 'varovani', label: 'Varování' }, { value: 'chyba', label: 'Chyba' },
            ]) },
            { id: 'revize', label: 'Revize', typ: 'volby', volby: sPocty('revize', [
                { value: 'aktualni', label: 'Do 6 měsíců' }, { value: 'stara', label: 'Starší než 6 měsíců' }, { value: 'nikdy', label: 'Nikdy' },
            ]) },
        ];
    }, [katStrom, prodejMap, pohledSada, filtry, odpovidaFiltrum, odpovidaVolbe]);

    const pocetFiltru = pocetAktivnichFiltru(skupinyFiltru, filtry);
    const zmenFiltr = (id, hodnota) => setFiltry(f => ({ ...f, [id]: hodnota }));
    const zrusFiltry = () => setFiltry(PRAZDNE_FILTRY);

    // Strom schová prázdné kategorie a vše rozbalí, jakmile se sada zúží.
    const filtrAktivni = searchActive || pohled !== 'aktivni' || pocetFiltru > 0;
    // Hromadné rozbalení dává smysl jen nad celým stromem — při zúžení je
    // rozbalený sám a archiv je plochý seznam.
    const rozbaleni = hasTree && !filtrAktivni && !vRezimuArchiv ? { vse: allExpanded, prepni: toggleAll } : null;

    const btnPrimary = {
        background: SECTION_COLOR, color: '#fff', border: 'none', borderRadius: 6,
        padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%',
    };

    const akce = [
        { id: 'novy', label: typ === 'SEM' ? 'Nový polotovar' : 'Nový produkt', Icon: Plus, onClick: onNew, primarni: true },
        { id: 'novaKat', label: 'Nová kategorie', Icon: FolderPlus, onClick: () => otevritNovouKat.current?.() },
    ];

    // Řádek archivu se kreslí naplocho — kategorie u vyřazených položek nic neříká.
    const archivRadek = (it) => {
        const grp = recGrouped?.get(recVariantKey(it)) ?? [];
        return (
            <RecipeListRow key={it.id} rec={it} siroky={siroky} prodej={prodejMap?.[it.id] || null} sloupce={sloupce}
                active={activeKey ? recVariantKey(it) === activeKey : activeId === it.id}
                varCount={grp.length} varianty={grp.map(r => r.varianta).filter(Boolean)}
                onClick={() => onSelect(it.id)}
                isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor} />
        );
    };

    return (
        <div className="rounded-xl overflow-hidden flex flex-col" style={{
            ...cardStyle, flex: 1, minHeight: 0,
        }}>
            <ListToolbar
                siroky={siroky} isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{
                    value: recSearch,
                    onChange: setRecSearch,
                    placeholder: `Hledat ${jednotneJm} / kód…`,
                    placeholderKratky: 'Hledat / kód…',
                }}
                akce={akce}
                pod={vyber.vybrane.size > 0 && (
                    <div className="flex items-center gap-2 rounded-lg px-2 py-1.5"
                        style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.30)' }}>
                        <span className="text-[11px] font-semibold" style={{ color: '#3B82F6' }}>
                            {pocetPolozek(vyber.vybrane.size)}
                        </span>
                        <span className="text-[11px] flex-1 truncate" style={{ color: mutedColor }}>přetáhni do kategorie</span>
                        <button onClick={vyber.zrus} title="Zrušit výběr (Esc)"
                            className="flex items-center justify-center rounded-md"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2, flexShrink: 0 }}>
                            <X size={13} />
                        </button>
                    </div>
                )}
                filtr={(
                    <FiltrTlacitko skupiny={skupinyFiltru} hodnoty={filtry} onChange={zmenFiltr} onReset={zrusFiltry}
                        pocetVysledku={zobrazene.length} siroky={siroky} isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
                pohled={(
                    <>
                        <PohledPill Icon={Check} label="Aktivní" pocet={pohledy.aktivni.length}
                            aktivni={pohled === 'aktivni'} onClick={() => setPohled('aktivni')}
                            title="Všechny položky v nabídce" isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                        <PohledPill Icon={TrendingDown} label={`Marže pod ${PRAH_MARZE.kriticka} %`} pocet={pohledy.marze.length}
                            aktivni={pohled === 'marze'} upozorneni onClick={() => setPohled('marze')}
                            title={`Položky, kde marže některého kanálu klesla pod ${PRAH_MARZE.kriticka} %`}
                            isDark={isDark} mutedColor={mutedColor} barva={MARZE_BARVY.kriticka} />
                        {pohledy.neprodava && (
                            <PohledPill Icon={PackageX} label="Neprodává se" pocet={pohledy.neprodava.length}
                                aktivni={pohled === 'neprodava'} onClick={() => setPohled('neprodava')}
                                title="Aktivní položky bez jediného prodeje za posledních 30 dní"
                                isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                        )}
                        <PohledPill Icon={Calculator} label="Bez kalkulace" pocet={pohledy.bezKalkulace.length}
                            aktivni={pohled === 'bezKalkulace'} upozorneni onClick={() => setPohled('bezKalkulace')}
                            title="Aktivní položky, u kterých chybí náklad"
                            isDark={isDark} mutedColor={mutedColor} barva={MARZE_BARVY.kriticka} />
                        <PohledPill Icon={Archive} label="Archiv" pocet={pohledy.archiv.length}
                            aktivni={vRezimuArchiv} onClick={() => setPohled('archiv')}
                            title="Vyřazené položky" isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                    </>
                )}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={skupinyFiltru} hodnoty={filtry} onChange={zmenFiltr} onReset={zrusFiltry}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
            />

            {!recLoading && zobrazene.length > 0 && (siroky
                ? <HlavickaMrizky isDark={isDark} sloupce={sloupce} rozbaleni={rozbaleni} />
                : <HlavickaUzka isDark={isDark} pocet={zobrazene.length} rozbaleni={rozbaleni} />)}

            {/* Strom + seznam */}
            <div className="overflow-y-auto flex-1" style={{ flex: 1, minHeight: 0, paddingTop: siroky ? 6 : 0 }}>
                {recLoading ? (
                    <ListSkeleton isDark={isDark} />
                ) : zobrazene.length === 0 ? (
                    pocetFiltru > 0 ? (
                        <div className="text-center py-8">
                            <p className="text-xs" style={{ color: mutedColor }}>Žádná položka neodpovídá filtru</p>
                            <button type="button" onClick={zrusFiltry} className="text-xs font-semibold mt-2"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR }}>Zrušit filtry</button>
                        </div>
                    ) : vRezimuArchiv ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Archiv je prázdný</p>
                    ) : pohled === 'marze' ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Žádná položka nemá marži pod {PRAH_MARZE.kriticka} %</p>
                    ) : pohled === 'neprodava' ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Všechny aktivní položky se za posledních 30 dní prodaly</p>
                    ) : pohled === 'bezKalkulace' ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Všechny aktivní položky mají kalkulaci</p>
                    ) : searchActive ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Nic nenalezeno pro „{recSearch}"</p>
                    ) : (
                        <div className="text-center px-6 py-10">
                            <FlaskConical size={28} style={{ color: mutedColor, opacity: 0.5, margin: '0 auto 10px' }} />
                            <p className="text-xs" style={{ color: mutedColor }}>Zatím žádné {typ === 'SEM' ? 'polotovary' : 'produkty'}</p>
                            <button onClick={onNew} className="mt-3 mx-auto" style={{ ...btnPrimary, width: 'auto', padding: '5px 12px' }}>
                                <Plus size={13} /> Vytvořit první
                            </button>
                        </div>
                    )
                ) : vRezimuArchiv ? (
                    <div style={{ opacity: 0.8 }}>{zobrazene.map(archivRadek)}</div>
                ) : (
                    <RecipeCategoryTree
                        onAiAnalyza={onAiAnalyza}
                        katStrom={katStrom} items={zobrazene} recGrouped={recGrouped}
                        activeKey={activeKey} activeId={activeId} onSelect={onSelect}
                        searchActive={filtrAktivni} typ={typ} siroky={siroky} prodejMap={prodejMap} sloupce={sloupce}
                        isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                        expanded={expanded} setExpanded={setExpanded}
                        vybrane={vyber.vybrane} onKlikRadek={vyber.klikNaRadek}
                        onCatSave={onCatSave} onCatAdd={onCatAdd} onCatDelete={onCatDelete}
                        onMoveItems={(ids, catId) => { onMoveItems?.(ids, catId); vyber.zrus(); }}
                        onReparentCat={onReparentCat}
                        onReorderCat={onReorderCat}
                        skrytPridatKategorii onRegisterNovaKategorie={registrujNovouKat}
                        onKategorieKlik={onKategorieKlik}
                        onUpravitKategorii={onUpravitKategorii} aktivniKategorieId={aktivniKategorieId}
                    />
                )}
            </div>

            {/* Combo skupiny — obdoba „Spravovat skupiny" u ingrediencí. Kde se
                combo nabízí, se nastavuje v záložce Combo kategorie a produktu. */}
            {typ === 'PRO' && onSpravovatComba && (
                <button type="button" onClick={onSpravovatComba} className="flex items-center gap-1.5 px-3 py-2 text-[11px] w-full"
                    style={{ color: mutedColor, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}
                    onMouseEnter={e => e.currentTarget.style.color = SECTION_COLOR}
                    onMouseLeave={e => e.currentTarget.style.color = mutedColor}>
                    <Gift size={13} /> Combo skupiny
                </button>
            )}
        </div>
    );
}
